import { useState, type FormEvent } from 'react';
import {
  Anchor,
  Button,
  Container,
  Fieldset,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';

export function Login() {
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'signed_in') return <Navigate to="/interactions" replace />;

  const state = location.state as { from?: string } | null;
  const goTo = state?.from ?? '/interactions';

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      void navigate(goTo, { replace: true });
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container component="main" size={1040} className="login-layout">
      <aside className="login-story">
        <Link to="/" className="login-wordmark">
          quizee
        </Link>
        <div>
          <p className="page-eyebrow">WELCOME TO YOUR EDITORIAL DESK</p>
          <h2>
            You bring
            <br />
            the story.
            <br />
            <em>
              They bring
              <br />
              the conversation.
            </em>
          </h2>
          <p>
            Polls, quizzes and a closer connection
            <br />
            to the people who read you.
          </p>
        </div>
        <span>Made for publishers. Built around readers.</span>
      </aside>
      <div className="login-form">
        <Text className="page-eyebrow">GOOD TO HAVE YOU BACK</Text>
        <Title order={1} mb="lg">
          Sign in
        </Title>
        <Text c="dimmed" size="sm" mb="lg">
          Your next great question starts here.
        </Text>
        {problem !== null && <ErrorNote message={problem} />}
        <form onSubmit={(event) => void submit(event)}>
          <Fieldset variant="unstyled" disabled={busy}>
            <Stack gap="md">
              <TextInput
                id="email"
                label="Email address"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <PasswordInput
                id="password"
                label="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Button type="submit" mt="xs">
                {busy ? 'Signing in...' : 'Sign in'}
              </Button>
            </Stack>
          </Fieldset>
        </form>
        <Text c="dimmed" size="sm" mt="lg">
          <Anchor component={Link} to="/forgot-password" size="sm">
            Forgotten your password?
          </Anchor>
        </Text>
        <Text c="dimmed" size="sm" mt="xs">
          No account yet?{' '}
          <Anchor component={Link} to="/signup" size="sm">
            Set up a publisher account
          </Anchor>
          .
        </Text>
      </div>
    </Container>
  );
}
